import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  AlertTriangle,
  HelpCircle,
  Clock,
  FileVideo,
  ArrowRight,
  Crown,
  Zap,
  Sparkles,
  AlertCircle,
  Receipt,
  RefreshCw,
  PlusCircle,
  CreditCard,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ensureMidtransSnapReady } from "@/utils/midtrans";
import { useTranslation } from "react-i18next";

const API = `${process.env.REACT_APP_API_URL}/api`;

const PLAN_COLORS = {
  free: "text-muted-foreground",
  starter: "text-emerald-400",
  pro: "text-brand-primary",
  business: "text-purple-400",
};
const DEFAULT_PLAN_LIMITS_MAP = {
  free: 3,
  starter: 25,
  pro: 80,
  business: 250,
  unlimited: -1,
};
const DEFAULT_PLAN_DAILY_LIMITS = {
  free: 1,
  starter: 5,
  pro: 15,
  business: 40,
  unlimited: Infinity,
};
const DEFAULT_PLAN_FILE_MB = {
  free: 50,
  starter: 200,
  pro: 500,
  business: 1024,
  unlimited: 2048,
};
const PLAN_ICONS = {
  starter: Sparkles,
  pro: Zap,
  business: Crown,
  unlimited: Crown,
};

const VERDICT_META = {
  REAL: {
    color: "text-verdict-real",
    border: "border-verdict-real/30",
    bg: "bg-verdict-real/5",
    icon: ShieldCheck,
  },
  FAKE: {
    color: "text-verdict-fake",
    border: "border-verdict-fake/30",
    bg: "bg-verdict-fake/5",
    icon: AlertTriangle,
  },
  UNCERTAIN: {
    color: "text-verdict-uncertain",
    border: "border-verdict-uncertain/30",
    bg: "bg-verdict-uncertain/5",
    icon: HelpCircle,
  },
};

function getDisplayLabel(verdict, confidence, t) {
  const v = (verdict || "UNCERTAIN").toUpperCase();
  const c = parseFloat(confidence) || 0;
  if (c < 60) return t("results.displayInsufficient");
  if (c < 80) return t("results.displayUncertain");
  if (v === "FAKE") return t("results.displayFake");
  if (v === "REAL") return t("results.displayReal");
  return t("results.displayUncertain");
}

function UsageRing({ used, limit }) {
  const isUnlimited = limit === Infinity || limit === -1;
  const pct = isUnlimited ? 0 : Math.min((used / limit) * 100, 100);
  const r = 52,
    circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  const color = isUnlimited
    ? "#14B8A6"
    : pct >= 100
      ? "#DC2626"
      : pct >= 80
        ? "#F59E0B"
        : "#059669";
  return (
    <svg width="140" height="140" viewBox="0 0 140 140">
      <circle
        cx="70"
        cy="70"
        r={r}
        fill="none"
        stroke="currentColor"
        strokeWidth="8"
        className="text-muted opacity-20"
      />
      <circle
        cx="70"
        cy="70"
        r={r}
        fill="none"
        stroke={color}
        strokeWidth="8"
        strokeDasharray={circ}
        strokeDashoffset={isUnlimited ? 0 : offset}
        strokeLinecap="round"
        transform="rotate(-90 70 70)"
        className="confidence-ring"
      />
      <text
        x="70"
        y="64"
        textAnchor="middle"
        fontSize="22"
        fontWeight="700"
        fontFamily="Outfit,sans-serif"
        fill={color}
      >
        {used}
      </text>
      <text
        x="70"
        y="81"
        textAnchor="middle"
        fontSize="10"
        fontFamily="JetBrains Mono,monospace"
        fill="#888"
        letterSpacing="1"
      >
        {isUnlimited ? "/ ∞" : `/ ${limit}`}
      </text>
    </svg>
  );
}

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08 } },
};
const item = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const { t, i18n } = useTranslation();
  const [analyses, setAnalyses] = useState([]);
  const [verifying, setVerifying] = useState(false);
  const [verifyFailed, setVerifyFailed] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const orderId = searchParams.get("order_id");
  const [packages, setPackages] = useState([]);
  const [showCredits, setShowCredits] = useState(false);
  const [buyingPack, setBuyingPack] = useState(null);
  const paymentParam = searchParams.get("payment");
  const creditsParam = searchParams.get("credits");
  const locale = i18n.language?.startsWith("en") ? "en-US" : "id-ID";
  const [planConfigs, setPlanConfigs] = useState({});

  useEffect(() => {
    axios
      .get(`${API}/history`, {
        params: { page: 1, page_size: 10 },
        withCredentials: true,
      })
      .then((r) => {
        const payload = r?.data;
        const items = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.items)
            ? payload.items
            : [];
        const sorted = [...items].sort(
          (a, b) =>
            new Date(b?.created_at || 0).getTime() -
            new Date(a?.created_at || 0).getTime(),
        );
        setAnalyses(sorted.slice(0, 10));
      })
      .catch(() => {});
  }, []);

  const pollPayment = useCallback(
    async (extId, attempts = 0) => {
      if (!extId) {
        setVerifying(false);
        setVerifyFailed(true);
        return;
      }
      if (attempts > 15) {
        setVerifying(false);
        setVerifyFailed(true);
        return;
      }
      try {
        const res = await axios.get(
          `${API}/subscription/payment-status/${extId}`,
          { withCredentials: true },
        );
        if (res.data.status === "PAID") {
          await refreshUser();
          setVerifying(false);
          setVerifyFailed(false);
          setSearchParams({});
          if ((res.data.credits || 0) > 0 && !res.data.plan_id) {
            toast.success(t("dashboard.credits.success"));
          } else {
            toast.success(t("dashboard.payment.success"));
          }
        } else if (res.data.status === "FAILED") {
          setVerifying(false);
          setVerifyFailed(false);
          setSearchParams({});
          toast.error(t("dashboard.payment.failed"));
        } else {
          setTimeout(() => pollPayment(extId, attempts + 1), 2000);
        }
      } catch (err) {
        if (err?.response?.status === 404 || err?.response?.status === 400) {
          setVerifying(false);
          setVerifyFailed(true);
          return;
        }
        setTimeout(() => pollPayment(extId, attempts + 1), 3000);
      }
    },
    [refreshUser, setSearchParams, t],
  );

  // Poll payment status after Midtrans redirect
  useEffect(() => {
    if (!orderId) return;
    const shouldPollPayment =
      (paymentParam === "success" || paymentParam === "pending") && !!orderId;
    const shouldPollCredits = creditsParam === "success" && !!orderId;
    if (!shouldPollPayment && !shouldPollCredits) return;
    setVerifying(true);
    setVerifyFailed(false);
    pollPayment(orderId);
  }, [orderId, paymentParam, creditsParam, pollPayment]);

  const buyCredits = useCallback(
    async (packId) => {
      setBuyingPack(packId);
      try {
        const res = await axios.post(
          `${API}/credits/purchase`,
          { pack_id: packId },
          { withCredentials: true },
        );
        if (res.data.snap_token) {
          try {
            await ensureMidtransSnapReady();
          } catch {
            toast.error(
              t("pricing.snapUnavailable", {
                defaultValue: "Payment popup unavailable. Please try again.",
              }),
            );
            setBuyingPack(null);
            return;
          }
          window.snap.pay(res.data.snap_token, {
            onSuccess: () => {
              setSearchParams({
                credits: "success",
                order_id: res.data.order_id,
              });
              setBuyingPack(null);
            },
            onPending: () => {
              toast.info(t("dashboard.credits.pending"));
              setSearchParams({
                credits: "pending",
                order_id: res.data.order_id,
              });
              setBuyingPack(null);
            },
            onError: () => {
              toast.error(t("dashboard.credits.failed"));
              setBuyingPack(null);
            },
            onClose: () => {
              setBuyingPack(null);
            },
          });
        } else if (res.data.redirect_url) {
          window.location.href = res.data.redirect_url;
        } else {
          toast.error(t("pricing.snapUnavailable"));
          setBuyingPack(null);
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || t("common.error"));
        setBuyingPack(null);
      }
    },
    [setSearchParams, t],
  );

  useEffect(() => {
    axios
      .get(`${API}/credits/packages`)
      .then((r) => setPackages(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/subscription/plans`, { withCredentials: true })
      .then((r) => {
        const plans = Array.isArray(r.data) ? r.data : [];
        const map = {};
        plans.forEach((p) => {
          if (p?.id) map[p.id] = p;
        });
        setPlanConfigs(map);
      })
      .catch(() => {});
  }, []);

  // Handle credits success
  useEffect(() => {
    if (creditsParam === "success" && orderId) return;

    if (creditsParam === "success") {
      toast.success(t("dashboard.credits.success"));
      refreshUser();
      setSearchParams({});
    }
    if (creditsParam === "pending") {
      toast.info(t("dashboard.credits.pending"));
      setSearchParams({});
    }
    if (creditsParam === "failed") {
      toast.error(t("dashboard.credits.failed"));
      setSearchParams({});
    }
  }, [creditsParam, orderId, refreshUser, setSearchParams, t]);

  useEffect(() => {
    if (paymentParam === "pending") {
      toast.info(t("dashboard.payment.pending"));
      setSearchParams({});
      return;
    }
    if (paymentParam === "failed") {
      toast.error(t("dashboard.payment.failed"));
      setSearchParams({});
      return;
    }
  }, [orderId, paymentParam, setSearchParams, t]);

  const plan = user?.plan || "free";
  const trialEndDate = user?.trial_end ? new Date(user.trial_end) : null;
  const isTrialActive =
    !!trialEndDate &&
    !Number.isNaN(trialEndDate.getTime()) &&
    trialEndDate > new Date() &&
    plan !== "free";
  const canBuyCredits = plan !== "free" && !isTrialActive;
  const trialEndLabel = isTrialActive
    ? trialEndDate.toLocaleString("id-ID", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Asia/Jakarta",
      })
    : null;
  const usageCount = user?.usage_count || 0;
  const isUnlimited = plan === "unlimited";
  const planConfig = planConfigs[plan] || {};
  const usageLimit = isUnlimited
    ? (user?.daily_budget_limit ?? planConfig.limit ?? Infinity)
    : (planConfig.limit ?? DEFAULT_PLAN_LIMITS_MAP[plan] ?? 3);
  const dailyLimit = isUnlimited
    ? (user?.daily_budget_limit ?? planConfig.daily_limit ?? Infinity)
    : (planConfig.daily_limit ?? DEFAULT_PLAN_DAILY_LIMITS[plan] ?? 1);
  const fileLimitMb = planConfig.file_limit_mb ?? DEFAULT_PLAN_FILE_MB[plan] ?? 50;
  const usagePct =
    usageLimit === Infinity ? 0 : (usageCount / usageLimit) * 100;
  const usagePctCapped = Math.min(usagePct, 100);
  const resetDate = user?.usage_reset_at
    ? new Date(user.usage_reset_at).toLocaleDateString(locale, {
        day: "numeric",
        month: "long",
      })
    : "—";
  const PlanIcon = PLAN_ICONS[plan];
  const planLabel = t(`dashboard.plan.label.${plan}`, { defaultValue: plan });
  const credits = user?.credits_balance || 0;
  const creditsExp = user?.credits_expires_at
    ? new Date(user.credits_expires_at).toLocaleDateString(locale, {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : null;

  const handleCancelSubscription = async () => {
    if (!window.confirm(t("dashboard.plan.cancelConfirm"))) return;
    try {
      const res = await axios.post(
        `${API}/subscription/cancel`,
        {},
        { withCredentials: true },
      );
      const effectiveAt = res?.data?.effective_at;
      if (effectiveAt) {
        const dateLabel = new Date(effectiveAt).toLocaleDateString(locale, {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
        toast.success(
          t("subscription.pendingDowngrade", {
            plan: t("dashboard.plan.label.free"),
            date: dateLabel,
          }),
        );
      } else {
        toast.success(t("dashboard.plan.cancelSuccess"));
      }
      refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    }
  };

  return (
    <main className="min-h-screen" data-testid="dashboard-page">
      {/* Payment verifying overlay */}
      {(verifying || verifyFailed) && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center">
          <div className="text-center space-y-4 p-8 rounded-md border border-border bg-card shadow-xl max-w-sm w-full mx-4">
            {verifyFailed ? (
              <>
                <AlertCircle className="w-10 h-10 text-verdict-uncertain mx-auto" />
                <div>
                  <p className="font-heading font-semibold">
                    {t("dashboard.verify.failed.title")}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t("dashboard.verify.failed.desc")}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setVerifyFailed(false);
                      setSearchParams({});
                    }}
                  >
                    {t("common.close")}
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1 bg-brand-primary hover:bg-brand-primary/90 text-white"
                    onClick={() => {
                      setVerifyFailed(false);
                      setVerifying(true);
                      pollPayment(orderId);
                    }}
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" />{" "}
                    {t("dashboard.verify.btn")}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="w-10 h-10 border-2 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto" />
                <div>
                  <p className="font-heading font-semibold">
                    {t("dashboard.verify.title")}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t("dashboard.verify.desc")}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex items-start justify-between"
        >
          <div>
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
              {t("dashboard.title")}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-1">
              {t("dashboard.welcome")}{" "}
              <span className="text-brand-primary">
                {user?.name?.split(" ")[0]}
              </span>
            </h1>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={refreshUser}
            title={t("dashboard.refresh")}
            className="mt-1"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </motion.div>

        {/* Quota warning */}
        {usagePctCapped >= 80 && usagePctCapped < 100 && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-5 flex items-center gap-3 p-3 rounded-md border border-verdict-uncertain/40 bg-verdict-uncertain/5 text-sm"
            data-testid="quota-warning"
          >
            <AlertCircle className="w-4 h-4 text-verdict-uncertain flex-shrink-0" />
            <span className="text-verdict-uncertain">
              {t("dashboard.warning.nearDetail", {
                used: usageCount,
                limit: usageLimit === Infinity ? "∞" : usageLimit,
              })}{" "}
              <Link to="/pricing" className="underline font-medium">
                {t("dashboard.warning.upgrade")}
              </Link>
            </span>
          </motion.div>
        )}
        {usagePctCapped >= 100 && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-5 flex items-center gap-3 p-3 rounded-md border border-verdict-fake/40 bg-verdict-fake/5 text-sm"
            data-testid="quota-exceeded"
          >
            <AlertCircle className="w-4 h-4 text-verdict-fake flex-shrink-0" />
            <span className="text-verdict-fake">
              {t("dashboard.warning.fullDetail", { date: resetDate })}{" "}
              <Link to="/pricing" className="underline font-medium">
                {t("dashboard.warning.upgradeToContinue")}
              </Link>
            </span>
          </motion.div>
        )}

        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5"
        >
          {/* Usage Card */}
          <motion.div
            variants={item}
            className="rounded-md border border-border bg-card p-6 flex flex-col items-center gap-3"
            data-testid="usage-card"
          >
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground self-start">
              {t("dashboard.usage.title")}
            </div>
            <UsageRing used={usageCount} limit={usageLimit} />
            <div className="w-full space-y-2 text-xs font-mono">
              <div className="flex justify-between text-muted-foreground">
                <span>{t("dashboard.usage.remaining")}</span>
                <span
                  className={
                    usagePctCapped >= 100
                      ? "text-verdict-fake"
                      : usagePctCapped >= 80
                        ? "text-verdict-uncertain"
                        : "text-verdict-real"
                  }
                >
                  {usageLimit === Infinity
                    ? "∞"
                    : Math.max(0, usageLimit - usageCount)}{" "}
                  {t("dashboard.usage.analyses")}
                </span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>{t("dashboard.usage.daily")}</span>
                <span>
                  {dailyLimit === Infinity ? "∞" : dailyLimit}
                  {t("dashboard.usage.perDay")}
                </span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>{t("dashboard.usage.fileLimit")}</span>
                <span>{fileLimitMb}MB</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>{t("dashboard.usage.reset")}</span>
                <span>{resetDate}</span>
              </div>
            </div>
          </motion.div>

          {/* Plan Card */}
          <motion.div
            variants={item}
            className="rounded-md border border-border bg-card p-6 flex flex-col justify-between"
            data-testid="plan-card"
          >
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">
                {t("dashboard.plan.title")}
              </div>
              <div
                className={`flex items-center gap-2 font-heading text-2xl font-bold ${PLAN_COLORS[plan]}`}
              >
                {PlanIcon && <PlanIcon className="w-5 h-5" />}
                {planLabel}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {isUnlimited
                  ? t("dashboard.plan.unlimited")
                  : `${usageLimit} ${t("dashboard.plan.perMonth")}`}
              </p>

              {/* Trial status */}
              {isTrialActive && (
                <div
                  className="mt-2 flex items-center gap-1.5 text-xs font-mono text-verdict-uncertain"
                  data-testid="trial-status"
                >
                  <Clock className="w-3 h-3" />
                  {t("dashboard.plan.trialUntil", {
                    defaultValue: `Trial — berakhir ${trialEndLabel}`,
                    datetime: trialEndLabel,
                  })}
                </div>
              )}

              {/* Subscription status */}
              {user?.recurring_plan_id && (
                <div
                  className="mt-3 space-y-1.5"
                  data-testid="recurring-status"
                >
                  <div
                    className={`flex items-center gap-1.5 text-xs font-mono ${
                      user.recurring_plan_status === "ACTIVE"
                        ? "text-verdict-real"
                        : user.recurring_plan_status === "FAILED"
                          ? "text-verdict-fake"
                          : "text-verdict-uncertain"
                    }`}
                  >
                    <RefreshCw className="w-3 h-3" />
                    {user.recurring_plan_status === "ACTIVE"
                      ? t("dashboard.plan.recurringActive")
                      : user.recurring_plan_status === "FAILED"
                        ? t("dashboard.plan.recurringFailed")
                        : t("dashboard.plan.recurringPending")}
                  </div>
                </div>
              )}

              <Link
                to="/payment-history"
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mt-3 transition-colors"
              >
                <Receipt className="w-3 h-3" />{" "}
                {t("dashboard.plan.paymentHistory")}
              </Link>
            </div>
            <div className="mt-4 flex flex-col gap-2">
              <Link to="/pricing" className="block">
                <Button
                  size="sm"
                  variant={plan === "free" ? "default" : "outline"}
                  className={`w-full rounded-sm gap-2 ${plan === "free" ? "bg-brand-primary hover:bg-brand-primary/90 text-white" : ""}`}
                  data-testid="upgrade-btn"
                >
                  {plan === "free" ? (
                    <>
                      <ArrowRight className="w-3.5 h-3.5" />{" "}
                      {t("dashboard.plan.upgrade")}
                    </>
                  ) : (
                    <>
                      <ArrowRight className="w-3.5 h-3.5" />{" "}
                      {t("dashboard.plan.change")}
                    </>
                  )}
                </Button>
              </Link>
              {user?.recurring_plan_id && plan !== "free" && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="w-full rounded-sm text-xs text-muted-foreground hover:text-verdict-fake"
                  onClick={handleCancelSubscription}
                  data-testid="cancel-sub-btn"
                >
                  {t("dashboard.plan.cancelSub")}
                </Button>
              )}
            </div>
          </motion.div>

          {/* Quick Action */}
          <motion.div
            variants={item}
            className="rounded-md border border-border bg-card p-6 flex flex-col justify-between"
            data-testid="quick-action-card"
          >
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">
                {t("dashboard.quickAction.title")}
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t("dashboard.quickAction.desc")}
              </p>
            </div>
            <Link to="/analyze" className="mt-4 block">
              <Button
                size="sm"
                disabled={usagePctCapped >= 100 && credits === 0}
                className="w-full rounded-sm bg-brand-primary hover:bg-brand-primary/90 text-white gap-2 disabled:opacity-50"
                data-testid="new-analysis-btn"
              >
                {t("dashboard.quickAction.btn")}{" "}
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </Link>
          </motion.div>

          {/* Credits Card */}
          <motion.div
            variants={item}
            className="rounded-md border border-border bg-card p-6 flex flex-col justify-between"
            data-testid="credits-card"
          >
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">
                {t("dashboard.credits.title")}
              </div>
              {credits > 0 ? (
                <div className="space-y-1">
                  <div className="flex items-end gap-1">
                    <span className="font-heading text-2xl font-bold text-brand-primary">
                      {credits}
                    </span>
                    <span className="text-xs text-muted-foreground mb-1">
                      {t("dashboard.credits.unit")}
                    </span>
                  </div>
                  {creditsExp && (
                    <p className="text-xs text-muted-foreground">
                      {t("dashboard.credits.expires")}: {creditsExp}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t("dashboard.credits.empty")}
                </p>
              )}
            </div>
            <div className="mt-4 space-y-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => canBuyCredits && setShowCredits(!showCredits)}
                disabled={!canBuyCredits}
                className="w-full rounded-sm gap-2 text-xs"
                data-testid="buy-credits-btn"
                title={
                  canBuyCredits
                    ? undefined
                    : isTrialActive
                      ? t("dashboard.credits.trialLocked", {
                          defaultValue:
                            "Kredit hanya tersedia untuk langganan berbayar aktif",
                        })
                      : t("dashboard.credits.freePlanLocked", {
                        defaultValue:
                          "Upgrade ke plan berbayar untuk membeli kredit",
                        })
                }
              >
                <PlusCircle className="w-3.5 h-3.5" />{" "}
                {t("dashboard.credits.buy")}
              </Button>
              {!canBuyCredits && (
                <p className="text-xs text-muted-foreground" data-testid="buy-credits-upgrade-hint">
                  {isTrialActive
                    ? t("dashboard.credits.trialLocked", {
                        defaultValue: "Kredit hanya tersedia untuk langganan berbayar aktif",
                      })
                    : t("dashboard.credits.freePlanLocked", {
                        defaultValue: "Upgrade ke plan berbayar untuk membeli kredit",
                      })}
                </p>
              )}
              {canBuyCredits && showCredits && (
                <div className="space-y-1.5 pt-1">
                  {packages.map((p) => (
                    <Button
                      key={p.id}
                      size="sm"
                      variant="ghost"
                      onClick={() => buyCredits(p.id)}
                      disabled={buyingPack === p.id}
                      className="w-full rounded-sm justify-between text-xs border border-border hover:border-brand-primary/40 h-auto py-2"
                      data-testid={`buy-${p.id}`}
                    >
                      <span className="flex items-center gap-1.5">
                        <CreditCard className="w-3 h-3" />
                        {p.label}
                      </span>
                      <span className="font-mono">
                        Rp {p.price_idr.toLocaleString("id-ID")}
                      </span>
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>

          {/* Recent Analyses */}
          <motion.div
            variants={item}
            className="md:col-span-2 xl:col-span-4 rounded-md border border-border bg-card p-6"
            data-testid="recent-analyses"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                {t("dashboard.recent.title")}
              </div>
              <Link
                to="/history"
                className="text-xs text-brand-primary hover:underline font-mono"
              >
                {t("dashboard.recent.seeAll")}
              </Link>
            </div>
            {analyses.length === 0 ? (
              <div
                className="text-center py-10 space-y-2"
                data-testid="no-analyses"
              >
                <FileVideo className="w-8 h-8 text-muted-foreground/30 mx-auto" />
                <p className="text-sm text-muted-foreground">
                  {t("dashboard.recent.empty")}
                </p>
                <Link to="/analyze">
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-sm mt-1"
                  >
                    {t("dashboard.recent.first")}
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="rounded-sm border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-secondary/20">
                      <tr className="border-b border-border">
                        <th className="px-3 py-2 text-left text-[11px] font-mono uppercase tracking-widest text-muted-foreground w-[96px]">
                          {t("dashboard.recent.colThumbnail")}
                        </th>
                        <th className="px-3 py-2 text-left text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
                          {t("dashboard.recent.colContent")}
                        </th>
                        <th className="px-3 py-2 text-left text-[11px] font-mono uppercase tracking-widest text-muted-foreground w-[180px]">
                          {t("dashboard.recent.colScore")}
                        </th>
                        <th className="px-3 py-2 text-left text-[11px] font-mono uppercase tracking-widest text-muted-foreground w-[170px]">
                          {t("dashboard.recent.colDate")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {analyses.map((a) => {
                        const meta =
                          VERDICT_META[a.verdict] || VERDICT_META.UNCERTAIN;
                        const verdictLabel = getDisplayLabel(
                          a.verdict,
                          a.confidence,
                          t,
                        );
                        const score = Math.round(parseFloat(a.confidence) || 0);
                        const title =
                          a.content_title ||
                          a.video_name ||
                          t("dashboard.recent.untitled");
                        const excerpt =
                          a.excerpt ||
                          a.summary ||
                          t("dashboard.recent.noExcerpt");
                        const dateLabel = a.created_at
                          ? new Date(a.created_at).toLocaleString(locale, {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "—";

                        return (
                          <tr
                            key={a.id}
                            className="border-b border-border/80 hover:bg-secondary/10 transition-colors"
                            data-testid={`recent-analysis-${a.id}`}
                          >
                            <td className="px-3 py-3 align-middle">
                              <Link
                                to={`/results/${a.id}`}
                                state={{ result: a }}
                                className="block w-20 h-12 rounded-sm overflow-hidden border border-border bg-secondary/20"
                              >
                                {a.thumbnail ? (
                                  <img
                                    src={a.thumbnail}
                                    alt={title}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-muted-foreground/60">
                                    <FileVideo className="w-4 h-4" />
                                  </div>
                                )}
                              </Link>
                            </td>
                            <td className="px-3 py-3 align-middle min-w-[280px]">
                              <Link
                                to={`/results/${a.id}`}
                                state={{ result: a }}
                                className="block"
                              >
                                <p className="font-medium line-clamp-1 hover:text-brand-primary transition-colors">
                                  {title}
                                </p>
                                <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                                  {excerpt}
                                </p>
                              </Link>
                            </td>
                            <td className="px-3 py-3 align-middle">
                              <span
                                className={`inline-flex items-center rounded-sm border px-2 py-1 text-xs font-mono ${meta.color} ${meta.border} ${meta.bg}`}
                              >
                                {verdictLabel} {score}%
                              </span>
                            </td>
                            <td className="px-3 py-3 align-middle text-xs text-muted-foreground font-mono whitespace-nowrap">
                              {dateLabel}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      </div>
    </main>
  );
}
